<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomThemesAPI from 'dashboard/api/customThemes';
import { customThemes, loadCustomThemes } from 'dashboard/helper/themeRegistry';
import { previewColorTheme, setColorTheme } from 'dashboard/helper/themeHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const { t } = useI18n();
const editingId = ref(null);
const saving = ref(false);

const lightDefaults = {
  background: '#e6e9ed',
  surface_1: '#f4f5f7',
  surface_2: '#ffffff',
  surface_active: '#ffffff',
  text_primary: '#202124',
  text_secondary: '#646464',
  text_muted: '#838383',
  border_container: '#d3d7dc',
  border_weak: '#dfe2e6',
  border_strong: '#c6cbd1',
  card: '#f4f5f7',
  button: '#ffffff',
  label: '#dfe3e8',
};

const darkDefaults = {
  background: '#1c1d20',
  surface_1: '#141517',
  surface_2: '#16171a',
  surface_active: '#353942',
  text_primary: '#edeef0',
  text_secondary: '#b0b4ba',
  text_muted: '#777b84',
  border_container: '#25282d',
  border_weak: '#1f1f25',
  border_strong: '#2e2d32',
  card: '#1c1e22',
  button: '#2a2b33',
  label: '#24262d',
};

const form = reactive({
  name: '',
  mode: 'light',
  colors: { ...lightDefaults },
});

const colorFields = [
  ['background', 'BACKGROUND'],
  ['surface_1', 'SURFACE_PRIMARY'],
  ['surface_2', 'SURFACE_SECONDARY'],
  ['surface_active', 'SURFACE_ACTIVE'],
  ['text_primary', 'TEXT_PRIMARY'],
  ['text_secondary', 'TEXT_SECONDARY'],
  ['text_muted', 'TEXT_MUTED'],
  ['border_container', 'BORDER_CONTAINER'],
  ['border_weak', 'BORDER_WEAK'],
  ['border_strong', 'BORDER_STRONG'],
  ['card', 'CARD'],
  ['button', 'BUTTON'],
  ['label', 'LABEL'],
];

const isEditing = computed(() => editingId.value !== null);
const isValid = computed(
  () =>
    form.name.trim() &&
    Object.values(form.colors).every(color => /^#[0-9a-f]{6}$/i.test(color))
);
const hasValidColors = computed(() =>
  Object.values(form.colors).every(color => /^#[0-9a-f]{6}$/i.test(color))
);

const restoreSelectedTheme = () => {
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
};

const beginNew = () => {
  editingId.value = 'new';
  form.name = '';
  form.mode = 'light';
  form.colors = { ...lightDefaults };
};

const beginEdit = theme => {
  editingId.value = theme.id;
  form.name = theme.name;
  form.mode = theme.mode;
  form.colors = { ...theme.colors };
};

const cancel = () => {
  editingId.value = null;
  restoreSelectedTheme();
};

const changeMode = () => {
  form.colors = {
    ...(form.mode === 'dark' ? darkDefaults : lightDefaults),
  };
};

const saveTheme = async () => {
  if (!isValid.value) return;
  saving.value = true;
  const payload = {
    name: form.name.trim(),
    mode: form.mode,
    colors: form.colors,
  };
  try {
    if (editingId.value === 'new') await CustomThemesAPI.create(payload);
    else await CustomThemesAPI.update(editingId.value, payload);
    await loadCustomThemes();
    editingId.value = null;
    restoreSelectedTheme();
    useAlert(t('BRANDING_SETTINGS.THEMES.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('BRANDING_SETTINGS.THEMES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const deleteTheme = async theme => {
  if (
    !window.confirm(
      t('BRANDING_SETTINGS.THEMES.DELETE_CONFIRM', { name: theme.name })
    )
  )
    return;
  try {
    await CustomThemesAPI.delete(theme.id);
    const selected = LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME);
    if (selected === `custom:${theme.id}`) {
      LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, 'auto');
    }
    await loadCustomThemes();
    if (editingId.value === theme.id) editingId.value = null;
    restoreSelectedTheme();
    useAlert(t('BRANDING_SETTINGS.THEMES.DELETE_SUCCESS'));
  } catch {
    useAlert(t('BRANDING_SETTINGS.THEMES.DELETE_ERROR'));
  }
};

watch(
  form,
  () => {
    if (isEditing.value && hasValidColors.value) previewColorTheme(form);
  },
  { deep: true }
);

onBeforeUnmount(restoreSelectedTheme);
</script>

<template>
  <section class="pt-8 mt-8 border-t border-n-weak">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('BRANDING_SETTINGS.THEMES.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('BRANDING_SETTINGS.THEMES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="!isEditing"
        icon="i-lucide-plus"
        :label="$t('BRANDING_SETTINGS.THEMES.NEW')"
        @click="beginNew"
      />
    </div>

    <div v-if="!isEditing" class="grid gap-3 mt-5 sm:grid-cols-2">
      <div
        v-for="theme in customThemes"
        :key="theme.id"
        class="flex items-center justify-between gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-center min-w-0 gap-3">
          <span
            class="size-9 border rounded-full border-n-strong shrink-0"
            :style="{ backgroundColor: theme.colors.background }"
          />
          <div class="min-w-0">
            <p class="font-medium truncate text-n-slate-12">{{ theme.name }}</p>
            <p class="text-xs text-n-slate-10">
              {{
                $t(`BRANDING_SETTINGS.THEMES.MODE_${theme.mode.toUpperCase()}`)
              }}
            </p>
          </div>
        </div>
        <div class="flex gap-1">
          <Button
            icon="i-lucide-pencil"
            slate
            faded
            sm
            @click="beginEdit(theme)"
          />
          <Button
            icon="i-lucide-trash-2"
            ruby
            faded
            sm
            @click="deleteTheme(theme)"
          />
        </div>
      </div>
      <p v-if="!customThemes.length" class="text-sm text-n-slate-10">
        {{ $t('BRANDING_SETTINGS.THEMES.EMPTY') }}
      </p>
    </div>

    <form v-else class="mt-6 space-y-6" @submit.prevent="saveTheme">
      <div class="grid gap-5 sm:grid-cols-2">
        <label class="space-y-2 text-sm text-n-slate-12">
          <span>{{ $t('BRANDING_SETTINGS.THEMES.NAME') }}</span>
          <input
            v-model="form.name"
            maxlength="40"
            required
            class="w-full h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2"
          />
        </label>
        <label class="space-y-2 text-sm text-n-slate-12">
          <span>{{ $t('BRANDING_SETTINGS.THEMES.MODE') }}</span>
          <select
            v-model="form.mode"
            class="w-full h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2"
            @change="changeMode"
          >
            <option value="light">
              {{ $t('BRANDING_SETTINGS.THEMES.MODE_LIGHT') }}
            </option>
            <option value="dark">
              {{ $t('BRANDING_SETTINGS.THEMES.MODE_DARK') }}
            </option>
          </select>
        </label>
      </div>

      <div>
        <h3 class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('BRANDING_SETTINGS.THEMES.COLORS') }}
        </h3>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label
            v-for="[field, label] in colorFields"
            :key="field"
            class="flex items-center gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
          >
            <input
              v-model="form.colors[field]"
              type="color"
              class="p-0 bg-transparent border-0 rounded size-8 shrink-0"
            />
            <span class="min-w-0 text-xs text-n-slate-11">
              <span class="block truncate">{{
                $t(`BRANDING_SETTINGS.THEMES.FIELD.${label}`)
              }}</span>
              <input
                v-model="form.colors[field]"
                maxlength="7"
                class="w-20 mt-1 font-mono text-xs bg-transparent text-n-slate-12"
              />
            </span>
          </label>
        </div>
      </div>

      <div class="p-4 border rounded-xl border-n-weak bg-n-surface-1">
        <p class="mb-3 text-xs font-medium uppercase text-n-slate-10">
          {{ $t('BRANDING_SETTINGS.THEMES.PREVIEW') }}
        </p>
        <div
          class="grid grid-cols-[8rem_1fr] min-h-36 overflow-hidden border rounded-lg border-n-weak bg-n-background"
        >
          <div class="p-3 space-y-2 border-r border-n-weak bg-n-surface-1">
            <div class="h-3 rounded bg-n-alpha-2" />
            <div class="h-7 rounded bg-n-solid-2" />
            <div class="h-7 rounded bg-n-alpha-1" />
          </div>
          <div class="p-4">
            <div
              class="max-w-xs p-3 border rounded-lg border-n-weak bg-n-card text-n-slate-12"
            >
              {{ $t('BRANDING_SETTINGS.THEMES.PREVIEW_MESSAGE') }}
              <p class="mt-1 text-xs text-n-slate-11">
                {{ $t('BRANDING_SETTINGS.THEMES.PREVIEW_DESCRIPTION') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex gap-2">
        <Button
          type="submit"
          :disabled="!isValid"
          :is-loading="saving"
          :label="$t('BRANDING_SETTINGS.THEMES.SAVE')"
        />
        <Button
          type="button"
          slate
          faded
          :label="$t('BRANDING_SETTINGS.THEMES.CANCEL')"
          @click="cancel"
        />
      </div>
    </form>
  </section>
</template>
